import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { BookOpen, Users, Clock, Plus, CheckCircle, Eye, Trash2, ClipboardCheck } from 'lucide-react';
import { TodoTaskModal } from '../modals/TodoTaskModal';
import { useConfirm } from '../ui/ConfirmDialog';
import { toast } from '../ui/Toaster';

interface TodoTask {
  id: string;
  title: string;
  description: string | null;
  target_type: string;
  created_at: string;
  due_date: string | null;
  courses: {
    name: string;
  };
  recipients_count: number;
  completed_count: number;
}

interface TaskRecipient {
  id: string;
  completed: boolean;
  completed_at: string | null;
  profiles: {
    full_name: string;
    email: string;
  };
}

export function DocenteDashboard() {
  const confirm = useConfirm();
  const { profile } = useAuth();
  const { navigateTo } = useNavigation();
  const [todoTasks, setTodoTasks] = useState<TodoTask[]>([]);
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const [taskRecipients, setTaskRecipients] = useState<Record<string, TaskRecipient[]>>({});
  const [stats, setStats] = useState({
    courses: 0,
    students: 0,
    pendingTasks: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile?.id) {
      loadData();
    }
  }, [profile?.id]);

  useEffect(() => {
    if (!profile?.id) return;

    const channel = supabase
      .channel('todo_tasks_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'todo_tasks',
          filter: `created_by=eq.${profile.id}`,
        },
        () => {
          loadTodoTasks();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'todo_task_recipients',
        },
        () => {
          loadTodoTasks();
          if (expandedTask) {
            loadTaskRecipients(expandedTask);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id, expandedTask]);

  const loadData = async () => {
    try {
      const [coursesRes] = await Promise.all([
        supabase.from('courses').select('*, course_enrollments(count)').or(`docente_id.eq.${profile!.id},secondary_docente_id.eq.${profile!.id}`),
      ]);

      const totalStudents = coursesRes.data?.reduce((sum, course: any) => {
        return sum + (course.course_enrollments?.[0]?.count || 0);
      }, 0) || 0;

      setStats({
        courses: coursesRes.data?.length || 0,
        students: totalStudents,
        pendingTasks: 0,
      });

      await loadTodoTasks();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTodoTasks = async () => {
    try {
      const { data: tasks, error } = await supabase
        .from('todo_tasks')
        .select(`
          id,
          title,
          description,
          target_type,
          created_at,
          due_date,
          courses(name)
        `)
        .eq('created_by', profile!.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const tasksWithCounts = await Promise.all(
        (tasks || []).map(async (task) => {
          const { data: recipients } = await supabase
            .from('todo_task_recipients')
            .select('id, completed')
            .eq('task_id', task.id);

          const recipientsCount = recipients?.length || 0;
          const completedCount = recipients?.filter(r => r.completed).length || 0;

          return {
            ...task,
            recipients_count: recipientsCount,
            completed_count: completedCount,
          };
        })
      );

      setTodoTasks(tasksWithCounts as TodoTask[]);
    } catch (error) {
      console.error('Error loading todo tasks:', error);
    }
  };

  const loadTaskRecipients = async (taskId: string) => {
    try {
      const { data, error } = await supabase
        .from('todo_task_recipients')
        .select(`
          id,
          completed,
          completed_at,
          profiles(full_name, email)
        `)
        .eq('task_id', taskId)
        .order('completed', { ascending: true });

      if (error) throw error;

      setTaskRecipients(prev => ({
        ...prev,
        [taskId]: data as TaskRecipient[],
      }));
    } catch (error) {
      console.error('Error loading task recipients:', error);
    }
  };

  const handleExpandTask = async (taskId: string) => {
    if (expandedTask === taskId) {
      setExpandedTask(null);
    } else {
      setExpandedTask(taskId);
      await loadTaskRecipients(taskId);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const ok = await confirm({
      title: 'Eliminar tarea',
      description: '¿Seguro que deseas eliminar esta tarea? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      const { error } = await supabase
        .from('todo_tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;

      await loadTodoTasks();
      toast.success('Tarea eliminada');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Error al eliminar la tarea');
    }
  };

  const getTargetTypeLabel = (targetType: string) => {
    switch (targetType) {
      case 'all':
        return 'Todo el curso';
      case 'individual':
        return 'Individual';
      case 'group':
        return 'Grupo';
      default:
        return targetType;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="absolute inset-0 bg-cubus-lime/30 blur-2xl rounded-full" />
          <div className="relative animate-spin rounded-full h-12 w-12 border-2 border-white/10 border-t-cubus-lime" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-slide-up p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <div className="relative overflow-hidden rounded-3xl glass-elevated p-8 border-gradient">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-cubus-lime/10 rounded-full blur-3xl" />
        <div className="relative">
          <div className="chip chip-lime mb-3">
            <span>Panel Docente</span>
          </div>
          <h1 className="text-display text-3xl md:text-4xl font-bold text-white tracking-tight mb-2">
            ¡Bienvenido, <span className="text-lime-glow">{profile?.full_name?.split(' ')[0]}</span>!
          </h1>
          <p className="text-white/60 max-w-xl">Gestiona tus cursos, tareas y actividades con tus alumnos.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="relative overflow-hidden glass-surface rounded-2xl p-5 glass-hover bg-gradient-to-br from-cubus-cyan/15 to-transparent">
          <div className="w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center text-cubus-cyan mb-3">
            <BookOpen className="w-5 h-5" />
          </div>
          <p className="text-xs text-white/50 font-medium mb-1">Cursos Asignados</p>
          <p className="text-display text-3xl font-bold text-white">{stats.courses}</p>
        </div>

        <div className="relative overflow-hidden glass-surface rounded-2xl p-5 glass-hover bg-gradient-to-br from-emerald-400/15 to-transparent">
          <div className="w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center text-emerald-400 mb-3">
            <Users className="w-5 h-5" />
          </div>
          <p className="text-xs text-white/50 font-medium mb-1">Total Estudiantes</p>
          <p className="text-display text-3xl font-bold text-white">{stats.students}</p>
        </div>

        <div className="relative overflow-hidden glass-surface rounded-2xl p-5 glass-hover bg-gradient-to-br from-cubus-lime/20 to-transparent">
          <div className="w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center text-cubus-lime mb-3">
            <Clock className="w-5 h-5" />
          </div>
          <p className="text-xs text-white/50 font-medium mb-1">Tareas Enviadas</p>
          <p className="text-display text-3xl font-bold text-white">{todoTasks.length}</p>
        </div>

        {/* Card Entregables — clickeable, navega a la vista del docente
            (lista de cursos → alumnos → entregable read-only). */}
        <button
          type="button"
          onClick={() => navigateTo('entregables')}
          className="relative overflow-hidden glass-surface rounded-2xl p-5 glass-hover bg-gradient-to-br from-cubus-lime/20 to-transparent text-left hover:border-cubus-lime/50 transition-all group"
          aria-label="Ver entregables de mis alumnos"
        >
          <div className="w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center text-cubus-lime mb-3 group-hover:scale-110 transition-transform">
            <ClipboardCheck className="w-5 h-5" />
          </div>
          <p className="text-xs text-white/50 font-medium mb-1">Entregables BIM</p>
          <p className="text-display text-2xl font-bold text-white group-hover:text-cubus-lime transition-colors">
            Ver progreso
          </p>
        </button>
      </div>

      <div className="glass-surface rounded-xl p-6 border border-white/[0.06]">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">TO-DO List</h3>
          <button
            onClick={() => setShowTodoModal(true)}
            className="flex items-center gap-2 px-4 py-2 btn-lime rounded-lg transition-all font-medium"
          >
            <Plus className="w-4 h-4" />
            Nueva Tarea
          </button>
        </div>

        {todoTasks.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-12 h-12 text-faint mx-auto mb-3" />
            <p className="text-muted mb-4">No has enviado tareas todavía</p>
            <button
              onClick={() => setShowTodoModal(true)}
              className="px-6 py-2 btn-lime rounded-lg transition-all font-medium inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Crear Primera Tarea
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {todoTasks.map((task) => (
              <div
                key={task.id}
                className="glass-deep rounded-lg border border-white/[0.08] overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-white mb-1">{task.title}</h4>
                      {task.description && (
                        <p className="text-sm text-muted mb-2 line-clamp-2">{task.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-subtle">
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-3 h-3" />
                          {task.courses.name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {getTargetTypeLabel(task.target_type)}
                        </span>
                        <span>
                          {new Date(task.created_at).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                        {task.due_date && (
                          <span className="text-cubus-lime">
                            Vence: {new Date(task.due_date).toLocaleDateString('es-ES', {
                              day: 'numeric',
                              month: 'short',
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-center px-3 py-2 glass-surface rounded-lg">
                        <div className="text-cubus-lime font-bold text-lg">
                          {task.completed_count}/{task.recipients_count}
                        </div>
                        <div className="text-xs text-muted">Completado</div>
                      </div>
                      <button
                        onClick={() => handleExpandTask(task.id)}
                        className="p-2 hover:bg-white/[0.06] rounded-lg transition-colors"
                        title="Ver detalles"
                      >
                        <Eye className="w-5 h-5 text-muted" />
                      </button>
                      <button
                        onClick={() => handleDeleteTask(task.id)}
                        className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Eliminar tarea"
                      >
                        <Trash2 className="w-5 h-5 text-red-400" />
                      </button>
                    </div>
                  </div>
                </div>

                {expandedTask === task.id && taskRecipients[task.id] && (
                  <div className="border-t border-white/[0.08] p-4 bg-white/[0.03]">
                    <h5 className="font-medium text-white mb-3 text-sm">
                      Estudiantes ({taskRecipients[task.id].length})
                    </h5>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {taskRecipients[task.id].map((recipient) => (
                        <div
                          key={recipient.id}
                          className="flex items-center justify-between p-2 glass-deep rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="text-white text-sm font-medium">
                              {recipient.profiles.full_name}
                            </div>
                            <div className="text-muted text-xs">{recipient.profiles.email}</div>
                          </div>
                          {recipient.completed ? (
                            <div className="flex items-center gap-2 text-cubus-lime text-sm">
                              <CheckCircle className="w-4 h-4" />
                              <span>
                                {recipient.completed_at &&
                                  new Date(recipient.completed_at).toLocaleDateString('es-ES', {
                                    day: 'numeric',
                                    month: 'short',
                                  })}
                              </span>
                            </div>
                          ) : (
                            <span className="text-subtle text-sm">Pendiente</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass-surface rounded-xl p-6 border border-white/[0.06]">
        <h3 className="text-xl font-bold text-white mb-4">Acceso Rápido</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            onClick={() => navigateTo('courses')}
            className="text-left px-4 py-3 bg-white/[0.02] hover:bg-white/[0.05] hover:border-cubus-lime/40 border border-white/[0.06] rounded-xl text-white transition-all duration-200 focus-lime"
          >
            <span className="font-medium">Ver Mis Cursos</span>
          </button>
          <button
            onClick={() => navigateTo('calendar')}
            className="text-left px-4 py-3 bg-white/[0.02] hover:bg-white/[0.05] hover:border-cubus-lime/40 border border-white/[0.06] rounded-xl text-white transition-all duration-200 focus-lime"
          >
            <span className="font-medium">Ver Calendario</span>
          </button>
          <button
            onClick={() => navigateTo('courses')}
            className="text-left px-4 py-3 bg-white/[0.02] hover:bg-white/[0.05] hover:border-cubus-lime/40 border border-white/[0.06] rounded-xl text-white transition-all duration-200 focus-lime"
          >
            <span className="font-medium">Gestionar Módulos</span>
          </button>
          <button
            onClick={() => navigateTo('chat')}
            className="text-left px-4 py-3 bg-white/[0.02] hover:bg-white/[0.05] hover:border-cubus-lime/40 border border-white/[0.06] rounded-xl text-white transition-all duration-200 focus-lime"
          >
            <span className="font-medium">Chat con Alumnos</span>
          </button>
        </div>
      </div>

      <TodoTaskModal
        isOpen={showTodoModal}
        onClose={() => setShowTodoModal(false)}
        onSuccess={() => loadTodoTasks()}
      />
    </div>
  );
}
